<template>
  <modal
    class="card_modal"
    :showModal="showModal"
    :showCloseButton="true"
    :floatCloseButton="true"
    @close="close"
  >
    <p class="modal_title">{{ $t('card_management__navigation') }}</p>

    <div v-for="method in paymentMethods" :key="method.id" class="card_item">
      <img class="payment_method_icon" :src="getPaymentMethodImg(method)" />
      <label :for="method.id">{{ getCardInfo(method) }}</label>
      <a
        @click="detachCard(method.id)"
        class="detach_link"
        :disabled="processing"
      >
        {{ $t('order_details__text_link__delete') }}
      </a>
    </div>

    <PrimaryButton
      @click.native="close()"
      class="modal_button"
      :disabled="processing"
    >
      {{ $t('card_management__button__completion') }}
    </PrimaryButton>
  </modal>
</template>

<script lang="ts">
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator'
import Modal from '@/components/Modal.vue'
import PrimaryButton from '@/components/PrimaryButton.vue'
import { api } from '@/api/appGatewayServerApi'
import * as models from '@/models'
import { showGenericNetworkError, sendApiWarning } from '@/utils/handle_error'
import { checkInMaintenance } from '@/utils/maintenance'

export const CardSaveLimit = 5

@Component({
  components: {
    Modal,
    PrimaryButton,
  },
})
export default class CardModal extends Vue {
  @Prop()
  showModal!: boolean

  @Prop()
  paymentMethods!: models.GamelibPaymentMethod[]

  @Prop()
  primaryMethodId!: string

  @Emit('close') // eslint-disable-next-line
  close() {}

  @Emit('initForm') // eslint-disable-next-line
  initForm() {}

  @Emit('updateMethods') // eslint-disable-next-line
  updateMethods(_methods: models.GamelibPaymentMethod[]) {}

  @Emit('updatePrimary') // eslint-disable-next-line
  updatePrimary(_primaryId: string) {}

  processing: boolean = false
  selectedMethodId: string = ''
  currentPrimaryId: string = ''
  defaultMethodLength: number = 0

  mounted() {
    this.init()
  }

  @Watch('showModal')
  onVisibilityChanged(newVal: boolean) {
    if (newVal) {
      this.init()
    }
  }

  init() {
    this.selectedMethodId = this.primaryMethodId
    this.currentPrimaryId = this.primaryMethodId
    this.defaultMethodLength = this.paymentMethods.length
  }

  get isFormInitRequired() {
    // デフォルトの決済手段を変更した
    const isPrimaryChanged = this.primaryMethodId !== this.currentPrimaryId

    // クレカ保存上限に達していた状態で削除を行った
    const isLimitReleased =
      this.defaultMethodLength === CardSaveLimit &&
      this.paymentMethods.length < CardSaveLimit

    return isPrimaryChanged || isLimitReleased
  }

  getCardInfo = models.getCardInfo
  getPaymentMethodImg = models.getPaymentMethodImg

  async detachCard(paymentMethodId: string) {
    if (this.processing) return
    this.processing = true

    try {
      // 指定したカードを削除する
      await api().streamer.currency.coin.method.detach({
        paymentMethodId,
      })

      const filtered = this.paymentMethods.filter(
        (v) => v.id !== paymentMethodId
      )

      if (filtered.length > 0) {
        const nextSelectId = filtered[0].id

        // 削除対象が primary な場合、primary を別のカードに変更
        if (this.currentPrimaryId === paymentMethodId) {
          await api().streamer.currency.coin.method.primary({
            paymentMethodId: nextSelectId,
          })
          this.currentPrimaryId = nextSelectId
        }

        this.selectedMethodId = nextSelectId
      } else {
        this.currentPrimaryId = ''
        this.selectedMethodId = ''
      }

      if (this.isFormInitRequired) {
        this.initForm()
      }

      // paymentMethods の更新を親に通知
      this.updateMethods(filtered)
      this.updatePrimary(this.currentPrimaryId)

      alert(this.$t('cardDeleted'))

      // すべての決済手段が削除された場合は、モーダルを閉じる
      if (filtered.length === 0) {
        this.close()
      }
    } catch (e) {
      if (checkInMaintenance(e)) {
        // メンテの場合はそのままメンテ中画面へリダイレクトさせる
        alert(this.$t('order_details__alert__cancel_operation'))
        this.$router.push({ path: '/maintenance' }).catch(() => {})
        return
      }
      sendApiWarning(e)
      showGenericNetworkError()
    }
    this.processing = false
  }
}
</script>

<style scoped>
.modal_title {
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  margin-bottom: 22px;
}

.card_item {
  text-align: left;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 20px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  & img {
    vertical-align: middle;
    border: 1px solid #e8e8e8;
    pointer-events: none; /* イベント発火防止用 */
  }
}

.card_item label {
  margin-left: 8px;
  pointer-events: none; /* イベント発火防止用 */
}

.detach_link {
  display: inline-block;
  margin-top: 12px;
  margin-left: 40px;
  color: red;
  margin-bottom: inherit;
  cursor: pointer;
}

.payment_method_icon {
  width: 40px;
  height: 24px;
  margin-right: 8px;
}

.card_modal >>> .modal__box {
  width: auto;
}
</style>
